import * as React from "react";
import { useGetInstance } from "../../Hooks/use-get";
import { useParams } from "react-router-dom";
import { PageLayout } from "../../Components/PageLayout";
import { Spinner } from "../../Components/LoadingAnimations/LoadingAnimations";
import { Card } from "../../Components/ui/card";
import { Form } from "../../Components/ui/form/form";
import { Input, Select } from "../../Components/ui/form";
import type { Location, LocationFormData, LocationPostData } from "./types";
import { timezones } from "./types";
import Error from "../../Components/ui/error/error";
import { useMutate } from "../../Hooks/use-mutate";
import { Button } from "../../Components/ui/button";

  const LocationRoute = () => {
  const { id } = useParams();
  const location = useGetInstance<Location>(`/assets/locations/v2/${id}/`);
  const yardConfig = location.data?.yard_config;

  const [locationFormData, setLocationFormData] =
    React.useState<LocationFormData | null>(null);

  const [yardConfigFormData, setYardConfigFormData] = React.useState<
    any | null
  >({
    reservationHours: yardConfig?.trailer_reservation_expiration_hours,
    isActive: yardConfig?.is_active,
  });

  const submitLocationData = useMutate<LocationPostData>(
    `/assets/locations/v2/${id}/`,
    "PATCH",
  );

  const createYardConfig = useMutate<any>(`/assets/yard-config/${id}/`, "POST");
  const updateYardConfig = useMutate<any>(
    `/assets/yard-config/${yardConfig?.id}/`,
    "PATCH",
  );
  const activateInventory = useMutate<any>(
    `/assets/location-functions`,
    "POST",
  );
  const deactivateInventory = useMutate<any>(
    `/assets/location-functions`,
    "PATCH",
  );

  React.useEffect(() => {
    setLocationFormData({
      name: location.data?.loc_name || "",
      street: location.data?.loc_street || "",
      city: location.data?.loc_city || "",
      state: location.data?.state || "",
      country: location.data?.loc_country || "",
      timezoneName: location.data?.timezone_name || "",
      isActive: location.data?.is_active === true,
      functions: location.data?.functions || [],
    });

    if (location.data?.yard_config) {
      setYardConfigFormData({
        reservationHours:
          location.data.yard_config.trailer_reservation_expiration_hours,
        isActive: location.data.yard_config.is_active,
      });
    }
  }, [location.data]);

  const handleFieldChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
    setState: React.Dispatch<React.SetStateAction<any>>,
  ) => {
    const { name, value } = e.target;

    setState((prevState: any) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.SubmitEvent) => {
    e.preventDefault();
    submitLocationData.mutate({
      loc_name: locationFormData?.name || "",
      loc_street: locationFormData?.street || "",
      loc_city: locationFormData?.city || "",
      state: locationFormData?.state || "",
      loc_country: locationFormData?.country || "",
      timezone_name: locationFormData?.timezoneName || "",
      is_active: locationFormData?.isActive || false,
    });
  };

  return (
    <PageLayout title={"Location Details"} header={""}>
      {location.isLoading ? (
        <Spinner />
      ) : (
        <>
          <Button
            variant="secondary"
            labelKey="back"
            onClick={() => window.history.back()}
          />
          {location.error && <Error message={location.error} />}
          {submitLocationData.error && (
            <Error message={submitLocationData.error} />
          )}
          {/* Location details form */}
          <Card title="Location Details" size="md">
            <Form
              onSubmit={handleSubmit}
              isLoading={submitLocationData.isLoading}
            >
              <Input
                name="name"
                label="name"
                value={locationFormData?.name || ""}
                onChange={(e) => handleFieldChange(e, setLocationFormData)}
              />
              <Input
                name="street"
                label="address"
                value={locationFormData?.street || ""}
                onChange={(e) => handleFieldChange(e, setLocationFormData)}
              />
              <Input
                name="city"
                label="city"
                value={locationFormData?.city || ""}
                onChange={(e) => handleFieldChange(e, setLocationFormData)}
              />
              <Input
                name="state"
                label="state"
                value={locationFormData?.state || ""}
                onChange={(e) => handleFieldChange(e, setLocationFormData)}
              />
              <Input
                name="country"
                label="country"
                value={locationFormData?.country || ""}
                onChange={(e) => handleFieldChange(e, setLocationFormData)}
              />
              <Select
                name="timezoneName"
                label="timezone"
                value={locationFormData?.timezoneName || ""}
                options={timezones}
                onChange={(e) => handleFieldChange(e, setLocationFormData)}
              />
              <Select
                name="isActive"
                label="status"
                value={String(locationFormData?.isActive ?? false)}
                options={[
                  { optionLabel: "active", optionValue: "true" },
                  { optionLabel: "inactive", optionValue: "false" },
                ]}
                onChange={(e) => handleFieldChange(e, setLocationFormData)}
              />
            </Form>
          </Card>

          <div className="flex justify-end mt-4"></div>

          {/* Yard settings card */}
          <Card title="Yard Configuration" titleKey="yardConfig" size="md">
            {!yardConfig ? (
              <Button
                variant="primary"
                labelKey="create"
                isLoading={createYardConfig.isLoading}
                onClick={() => createYardConfig.mutate({ location: id })}
              />
            ) : (
              <>
                <Form
                  isLoading={updateYardConfig.isLoading}
                  onSubmit={(e) => {
                    e.preventDefault();
                    updateYardConfig.mutate({
                      trailer_reservation_expiration_hours:
                        yardConfigFormData?.reservationHours || 0,
                      is_active: yardConfigFormData?.isActive === "true",
                    });
                  }}
                >
                  <Input
                    type="number"
                    name="reservationHours"
                    label="reservationExpiration"
                    value={yardConfigFormData?.reservationHours || 0}
                    min={0}
                    onChange={(e) =>
                      handleFieldChange(e, setYardConfigFormData)
                    }
                  />
                  <Select
                    name="isActive"
                    label="status"
                    value={yardConfigFormData.isActive}
                    options={[
                      { optionLabel: "active", optionValue: "true" },
                      { optionLabel: "inactive", optionValue: "false" },
                    ]}
                    onChange={(e) =>
                      handleFieldChange(e, setYardConfigFormData)
                    }
                  />
                </Form>
              </>
            )}
          </Card>
          <div className="flex justify-end mt-4"></div>
          <Card
            title="Inventory configuration"
            titleKey="inventoryConfig"
            size="md"
          >
            {location?.data?.functions.find(
              (f: any) => f.code === "inventory",
            ) ? (
              <Button
                variant="primary"
                labelKey="deactivate"
                isLoading={deactivateInventory.isLoading}
                onClick={() =>
                  deactivateInventory.mutate({
                    location: id,
                    function_code: "inventory",
                  })
                }
              />
            ) : (
              <Button
                variant="primary"
                labelKey="activate"
                isLoading={activateInventory.isLoading}
                onClick={() =>
                  activateInventory.mutate({
                    location: id,
                    function_code: "inventory",
                  })
                }
              />
            )}
          </Card>
        </>
      )}
    </PageLayout>
  );
};

export default LocationRoute;
